import React, { useEffect, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { roomDefinitionsTooltip } from "../../../constants/TooltipInformation";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import {
  payloadConstants,
  roomDefinitionValues,
} from "../../../constants/defaultValues";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const normalizeSectionRows = (value: unknown): any[] => {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const RoomtypeDefinition = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [roomType, setRoomType] = useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = useState<boolean>(false);
  const [roomDescription, setRoomDescription] = useState<string | null>(null);
  const [building, setBuilding] = useState<string[]>([]);
  const [suite, setSuite] = useState<boolean>(false);
  const [condo, setCondo] = useState<boolean>(false);
  const [condoDescription, setCondoDescription] = useState<string | null>(null);
  const [condoFirstName, setCondoFirstName] = useState<string | null>(null);
  const [condoLastName, setCondoLastName] = useState<string | null>(null);
  const [condoNightAvailable, setCondoNightAvailable] =
    useState<boolean>(false);
  const [condoRentedToGuest, setCondoRentedToGuest] = useState<boolean>(false);
  const [condoRendedToOwner, setCondoRendedToOwner] = useState<boolean>(false);
  const [condoRoomRevenue, setCondoRoomRevenue] = useState<number | null>(null);
  const [condoUnitCondition, setCondoUnitCondition] = useState<string | null>(
    null,
  );
  const [condoAddress, setCondoAddress] = useState<string | null>(null);
  const [condoContactDetails, setCondoContactDetails] = useState<string | null>(
    null,
  );
  const [bedCount, setBedCount] = useState<number | null>(null);
  const [allowPets, setAllowPets] = useState<boolean>(false);
  const [petCount, setPetCount] = useState<number | null>(null);
  const [roomCount, setRoomCount] = useState<number | null>(null);
  const [smokingRoom, setSmokingRoom] = useState<boolean>(false);
  const [connectingRooms, setConnectingRooms] = useState<string[] | null>(null);
  const [housekeepingSection, setHousekeepingSection] = useState<string | null>(
    null,
  );
  const [housekeepingList, setHouseKeepingList] = useState<string[]>([]);
  const [stayoverPoints, setStayoverPoints] = useState<number | null>(null);
  const [checkoutPoints, setCheckoutPoints] = useState<number | null>(null);
  const [roomFeatures, setRoomFeatures] = useState<string[] | null>(null);
  const [roomLocation, setRoomLocation] = useState<string[] | null>(null);
  const [roomDefinitionInformation, setRoomDefinitionInformation] = useState<
    any[]
  >(() =>
    normalizeSectionRows(fieldValues?.[RoomPropertyEnum.roomDefinitions]),
  );
  const hasSyncedToParentRef = useRef(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [buildingList, setBuildingList] = useState<string[]>([]);
  const [roomTypeList, setRoomTypeList] = useState<string[]>([]);
  const [roomFeaturesList, setRoomFeaturesList] = useState<string[]>([]);
  const [roomLocationList, setRoomLocationList] = useState<string[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const connectingRoomsList =
    roomDefinitionInformation?.flatMap((room: any) => room.roomNumber) ?? [];
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const roomDefinitionPayload = {
    roomNumber: roomCode,
    roomTypeCode: roomType,
    buildingCode: building.map((code) =>
      typeof code === "string" && code.includes(" - ")
        ? code.split(" - ")[0].trim()
        : code,
    ),
    // description:
    //   roomDescription ??
    //   payloadConstants[RoomPropertyEnum.roomDefinitions].ROOM_DESCRIPTION,
    isSuite: suite,
    // beds: bedCount ?? 0,
    connectingRoomCodes: connectingRooms ?? [],
    stayOverPoints:
      stayoverPoints ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].STAYOVER_POINTS,
    checkPoints:
      checkoutPoints ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CHECKOUT_POINTS,
    housekeepingSection:
      housekeepingSection ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].HOUSEKEEPING_POINTS,
    roomFeatures: roomFeatures ?? [],
    roomLocations: roomLocation ?? [],
    condo: condo ?? false,
    condoDescription:
      condoDescription ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_DESCRIPTION,
    condoFirstName:
      condoFirstName ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_FIRST_NAME,
    condoLastName:
      condoLastName ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_LAST_NAME,
    condoNightAvailable: condoNightAvailable ?? false,
    condoRentedToGuest: condoRentedToGuest ?? false,
    condoRendedToOwner: condoRendedToOwner ?? false,
    condoRoomRevenue:
      condoRoomRevenue ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_ROOM_REVENUE,
    condoUnitCondition:
      condoUnitCondition ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_UNIT_CONDITION,
    condoAddress:
      condoAddress ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_ADDRESS,
    condoContactDetails:
      condoContactDetails ??
      payloadConstants[RoomPropertyEnum.roomDefinitions].CONDO_CONTACT_DETAILS,
    isSmoking: smokingRoom,
    // allowPets: allowPets ?? false,
    // petCount: petCount ?? false,
    roomCount: roomCount ?? false,
    isActive: active,
  };

  const addRoomDefinition = () => {
    if (validateInput()) {
      if (!roomCode || roomCode === null || roomCode === "") {
        setCodeError(true);
      }
      if (!roomType || roomType === null || roomType === "") {
        setAlertMessage("Enter the room type");
        setAlertType("error");
        setOpenAlert(true);
        return;
      }
      if (!building?.length) {
        setAlertMessage("Enter the building");
        setAlertType("error");
        setOpenAlert(true);
        return;
      } else if (
        checkDuplicate(
          Array.isArray(roomDefinitionInformation)
            ? roomDefinitionInformation
            : [],
          DNDFieldsEnum.ROOMDEFINITIONCODE,
          roomCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setRoomDefinitionInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...roomDefinitionPayload,
                    ...roomDefinitionValues,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...roomDefinitionPayload,
                    ...roomDefinitionValues,
                  },
                ],
          );
        });
        setLastUpdatedRecord(roomDefinitionPayload);
        editRoomDefinition(null);
        setEditClicked(false);
      }
    }
  };

  const editRoomDefinition = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setRoomCode(list?.roomNumber);
    setRoomType(list?.roomTypeCode);
    setBuilding(
      list?.buildingCode
        ? (Array.isArray(list.buildingCode)
            ? list.buildingCode
            : [list.buildingCode]
          ).map((code: string) =>
            typeof code === "string" && code.includes(" - ")
              ? code.split(" - ")[0].trim()
              : code,
          )
        : [],
    );
    setRoomDescription(list?.description);
    setSuite(list ? list.isSuite : false);
    setBedCount(list?.beds);
    setConnectingRooms(list?.connectingRoomCodes);
    setStayoverPoints(list?.stayOverPoints);
    setCheckoutPoints(list?.checkPoints);
    setHousekeepingSection(list?.housekeepingSection);
    setRoomFeatures(list?.roomFeatures);
    setRoomLocation(list?.roomLocations);
    setCondoDescription(list?.condoDescription);
    setCondoFirstName(list?.condoFirstName);
    setCondoLastName(list?.condoLastName);
    setCondoNightAvailable(list?.condoNightAvailable);
    setCondoRentedToGuest(list?.condoRentedToGuest);
    setCondoRendedToOwner(list?.condoRendedToOwner);
    setCondoRoomRevenue(list?.condoRoomRevenue);
    setCondoUnitCondition(list?.condoUnitCondition);
    setCondoAddress(list?.condoAddress);
    setCondoContactDetails(list?.condoContactDetails);
    setSmokingRoom(list ? list.isSmoking : false);
    setCondo(list ? list.condo : false);
    setAllowPets(list ? list.allowPets : false);
    setPetCount(list?.petCount);
    setRoomCount(list?.roomCount);
    setActive(list ? list.isActive : true);
  };

  const updateRoomDefinition = () => {
    if (!validateInput()) return;
    if (!roomCode || roomCode === null || roomCode === "") {
      setCodeError(true);
      return;
    }
    if (!roomType || roomType === null || roomType === "") {
      setAlertMessage("Enter the room type");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!building?.length) {
      setAlertMessage("Enter the building");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(roomDefinitionInformation)
          ? roomDefinitionInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.ROOMDEFINITIONCODE,
        roomCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRoomDefinitionInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...roomDefinitionPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editRoomDefinition(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.roomDefinitions} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteRoomDefinition = (id: number) => {
    setRoomDefinitionInformation(deleteItem(roomDefinitionInformation, id));
  };

  const validateInput = () => {
    setCodeError(false);
    if (
      roomCode &&
      !validateString(
        roomCode,
        validationLength.roomTypeDefinitionCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!hasSyncedToParentRef.current) {
      hasSyncedToParentRef.current = true;
      return;
    }
    data(roomDefinitionInformation);
    editClicked &&
      setSelectedId(
        checkData(roomDefinitionInformation, selectedId) ? selectedId : null,
      );
  }, [roomDefinitionInformation]);

  useEffect(() => {
    validateInput();
  }, [roomCode]);

  useEffect(() => {
    const roomTypeError = !roomType || roomType === null || roomType === "";
    const buildingError = !building?.length;
    if (roomTypeError || buildingError || codeError || Boolean(selectedId)) {
      setOpenAccordion(true);
    }
  }, [building, codeError, roomType, selectedId]);

  useEffect(() => {
    if (!fieldValues) return;
    setRoomDefinitionInformation(
      normalizeSectionRows(fieldValues[RoomPropertyEnum.roomDefinitions]),
    );
    setBuildingList(
      fillAutocompleteValues(
        fieldValues[RoomPropertyEnum.buildingCodes],
        DNDFieldsEnum.BUILDINGCODE,
        "",
      ),
    );
    setRoomTypeList(
      fillAutocompleteValues(
        fieldValues[RoomPropertyEnum.roomTypeMaintainence],
        DNDFieldsEnum.ROOMTYPECODE,
        DNDFieldsEnum.ROOMTYPENAME,
      ),
    );
    setRoomFeaturesList(
      fillAutocompleteValues(
        fieldValues[RoomPropertyEnum.roomFeatures],
        DNDFieldsEnum.ROOMFEATURECODE,
        DNDFieldsEnum.ROOMFEATURENAME,
      ),
    );
    setRoomLocationList(
      fillAutocompleteValues(
        fieldValues[RoomPropertyEnum.roomLocation],
        DNDFieldsEnum.ROOMLOCATIONCODE,
        DNDFieldsEnum.ROOMLOCATIONNAME,
      ),
    );
    setHouseKeepingList(
      fillAutocompleteValues(
        fieldValues[RoomPropertyEnum.HousekeepingSections],
        DNDFieldsEnum.HOUSEKEEPINGCODE,
        DNDFieldsEnum.HOUSEKEEPINGNAME,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once when screen opens
  }, []);

  const roomCodeHelperText = codeError
    ? !(roomCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomTypeDefinitions.roomCode
    : versaFieldHelperText.roomTypeDefinitions.roomCode;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            gap="10px"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Room Definition Information
              <Tooltip title={tooltipComponent(roomDefinitionsTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editRoomDefinition(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRoomDefinition(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Accordion
            expanded={openAccordion}
            onChange={() => setOpenAccordion(!openAccordion)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Room Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="column" width="100%" gap="20px">
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  gap="20px"
                  width="100%"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Room Number
                      <Tooltip
                        title={tooltipComponent(
                          roomDefinitionsTooltip.roomNumber,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="roomCode"
                      variant="outlined"
                      placeholder="Room Number ?"
                      value={roomCode ?? ""}
                      error={codeError}
                      sx={{ width: "100%" }}
                      helperText={roomCodeHelperText}
                      onChange={(e: any) => {
                        setRoomCode(e.target.value);
                        setCodeError(false);
                      }}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Room Type
                      <Tooltip
                        title={tooltipComponent(
                          roomDefinitionsTooltip.roomType,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Autocomplete
                      id="roomType"
                      disablePortal
                      options={removeSampleTestArray(roomTypeList)}
                      value={roomType ?? ""}
                      sx={{ width: "100%" }}
                      disabled={!roomTypeList}
                      onChange={(e: any) => setRoomType(e.target.innerText)}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Room Type" />
                      )}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Buildings
                      <Tooltip
                        title={tooltipComponent(
                          roomDefinitionsTooltip.building,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Autocomplete
                      id="buildingName"
                      multiple
                      disablePortal
                      options={removeSampleTestArray(buildingList)}
                      value={building}
                      sx={{ width: "100%" }}
                      disabled={!buildingList}
                      onChange={(_, newValue) => setBuilding(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buildings"
                          id="buildingName2"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
                {/* <Stack direction="row" width="100%" gap="5px">
                  <TextField
                    id="roomDescription"
                    variant="outlined"
                    placeholder="Room Description ?"
                    value={roomDescription ?? ""}
                    sx={{ width: "100%" }}
                    onChange={(e: any) => {
                      setRoomDescription(e.target.value);
                    }}
                  />
                  <Tooltip title={tooltipComponent("")}>
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Stack> */}
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  flexWrap="wrap"
                  p={1}
                  width="100%"
                  gap="20px"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="suiteCheckbox"
                        checked={suite}
                        onChange={(e: any) => setSuite(e.target.checked)}
                      />
                    }
                    label="Suite ?"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="condoCheckbox"
                        checked={condo}
                        onChange={(e: any) => setCondo(e.target.checked)}
                      />
                    }
                    label="Condo ?"
                  />
                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        id="allowPetsCheckbox"
                        checked={allowPets}
                        onChange={(e: any) => setAllowPets(e.target.checked)}
                      />
                    }
                    label="Allow Pets ?"
                  /> */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="smokingRoomCheckbox"
                        checked={smokingRoom}
                        onChange={(e: any) => setSmokingRoom(e.target.checked)}
                      />
                    }
                    label="Smoking Room ?"
                  />
                </Stack>
                {condo && (
                  <Stack direction="column" width="100%" gap="20px">
                    <Stack direction="row" width="100%" gap="10px">
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoDescription"
                          variant="outlined"
                          placeholder="Condo Description"
                          value={condoDescription ?? ""}
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoDescription(e.target.value);
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoFirstName"
                          variant="outlined"
                          placeholder="Condo First Name"
                          value={condoFirstName ?? ""}
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoFirstName(e.target.value);
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoLastName"
                          variant="outlined"
                          placeholder="Condo Last Name"
                          value={condoLastName ?? ""}
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoLastName(e.target.value);
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                    </Stack>
                    <Stack direction="row" width="100%" gap="10px">
                      <Stack direction="row" width="100%" gap="5px">
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="condoNightAvailable"
                              checked={condoNightAvailable}
                              onChange={(e: any) =>
                                setCondoNightAvailable(e.target.checked)
                              }
                            />
                          }
                          label="Condo Night Available ?"
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="condoRentedToGuest"
                              checked={condoRentedToGuest}
                              onChange={(e: any) =>
                                setCondoRentedToGuest(e.target.checked)
                              }
                            />
                          }
                          label="Condo Rented to Guest ?"
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="condoRentedToOwner"
                              checked={condoRendedToOwner}
                              onChange={(e: any) =>
                                setCondoRendedToOwner(e.target.checked)
                              }
                            />
                          }
                          label="Condo Rented to Owner ?"
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                    </Stack>
                    <Stack direction="row" width="100%" gap="10px">
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoRoomRevenue"
                          variant="outlined"
                          placeholder="Condo Room Revenue"
                          value={condoRoomRevenue ?? ""}
                          type="number"
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoRoomRevenue(
                              e.target.value > 0 ? e.target.value : null,
                            );
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoUnitCondition"
                          variant="outlined"
                          placeholder="Condo Unit Condition"
                          value={condoUnitCondition ?? ""}
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoUnitCondition(e.target.value);
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoAddress"
                          variant="outlined"
                          placeholder="Condo Address"
                          value={condoAddress ?? ""}
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoAddress(e.target.value);
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                      <Stack direction="row" width="100%" gap="5px">
                        <TextField
                          id="condoContactDetails"
                          variant="outlined"
                          placeholder="Condo Contact Details"
                          value={condoContactDetails ?? ""}
                          sx={{ width: "100%" }}
                          onChange={(e: any) => {
                            setCondoContactDetails(e.target.value);
                          }}
                        />
                        {/* <Tooltip title={tooltipComponent("")}>
                          <InfoIcon fontSize="small" />
                        </Tooltip> */}
                      </Stack>
                    </Stack>
                  </Stack>
                )}
                <Stack className="form-two-col-row" direction="row" gap="20px">
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Number of Beds
                      <Tooltip
                        title={tooltipComponent(
                          roomDefinitionsTooltip.numberOfBeds,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="noOfBeds"
                      variant="outlined"
                      placeholder="Number of Beds in Room ?"
                      value={bedCount ?? ""}
                      type="number"
                      sx={{ width: "100%" }}
                      helperText={roomDefinitionsTooltip.numberOfBeds}
                      onChange={(e: any) => {
                        setBedCount(e.target.value > 0 ? e.target.value : null);
                      }}
                    />
                  </Stack>
                  {/* <Stack direction="row" width="100%" gap="5px">
                    <TextField
                      id="howManyPets"
                      variant="outlined"
                      placeholder="How many pets ?"
                      type="number"
                      value={petCount ?? ""}
                      sx={{ width: "100%" }}
                      helperText={roomDefinitionsTooltip.petCount}
                      onChange={(e: any) => {
                        setPetCount(
                          e.target.value >= 0 ? e.target.value : null,
                        );
                      }}
                    />
                    <Tooltip
                      title={tooltipComponent(roomDefinitionsTooltip.petCount)}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack> */}
                  {/* <Stack direction="row" width="100%" gap="5px">
                    <TextField
                      id="noOfRoomsForOccupancyTax"
                      variant="outlined"
                      placeholder="Number of room for occupancy tax?"
                      value={roomCount ?? ""}
                      type="number"
                      sx={{ width: "100%" }}
                      helperText={roomDefinitionsTooltip.numberOfBeds}
                      onChange={(e: any) => {
                        setRoomCount(
                          e.target.value > 0 ? e.target.value : null,
                        );
                      }}
                    />
                    <Tooltip
                      title={tooltipComponent(
                        roomDefinitionsTooltip.numberOfBeds,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack> */}
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Connecting Rooms
                      <Tooltip
                        title={tooltipComponent(
                          roomDefinitionsTooltip.connectingRooms,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Autocomplete
                      id="connectingRooms"
                      multiple
                      disablePortal
                      options={removeSampleTestArray(connectingRoomsList)}
                      value={connectingRooms ?? []}
                      sx={{ width: "100%" }}
                      disabled={!roomDefinitionInformation}
                      onChange={(_, newValue) => {
                        setConnectingRooms(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Connecting Rooms" />
                      )}
                    />
                  </Stack>
                </Stack>
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  gap="20px"
                  width="100%"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">Room Features</FormLabel>
                    <Autocomplete
                      id="roomFeatures"
                      multiple
                      disablePortal
                      options={removeSampleTestArray(roomFeaturesList)}
                      value={roomFeatures ?? []}
                      sx={{ width: "100%" }}
                      disabled={!roomFeaturesList}
                      onChange={(event, newValue) => {
                        setRoomFeatures(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Room features" />
                      )}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">Room Locations</FormLabel>
                    <Autocomplete
                      multiple
                      id="roomLocations"
                      disablePortal
                      options={removeSampleTestArray(roomLocationList)}
                      value={roomLocation ?? []}
                      sx={{ width: "100%" }}
                      disabled={!roomLocationList}
                      onChange={(event, newValue) => {
                        setRoomLocation(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Room locations" />
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Points Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                className="form-two-col-row"
                direction="row"
                width="100%"
                gap="20px"
              >
                <Stack direction="column" width="100%" gap="5px">
                  <FormLabel component="legend">Housekeeping Section</FormLabel>
                  <Autocomplete
                    id="housekeepingSection"
                    disablePortal
                    options={removeSampleTestArray(housekeepingList)}
                    value={housekeepingSection ?? ""}
                    sx={{ width: "100%" }}
                    disabled={!housekeepingList}
                    onChange={(e: any) =>
                      setHousekeepingSection(e.target.innerText)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Housekeeping Section"
                      />
                    )}
                  />
                </Stack>
                <Stack direction="column" width="100%" gap="5px">
                  <FormLabel component="legend">Stayover Points</FormLabel>
                  <TextField
                    id="stayOverPoints"
                    variant="outlined"
                    type="number"
                    value={stayoverPoints ?? ""}
                    placeholder="Stayover Points?"
                    sx={{ width: "100%" }}
                    onChange={(e: any) => {
                      setStayoverPoints(
                        e.target.value >= 0 ? e.target.value : null,
                      );
                    }}
                  />
                </Stack>
                <Stack direction="column" width="100%" gap="5px">
                  <FormLabel component="legend">Checkout Points</FormLabel>
                  <TextField
                    id="checkoutPoints"
                    variant="outlined"
                    type="number"
                    value={checkoutPoints ?? ""}
                    placeholder="Checkout Points?"
                    sx={{ width: "100%" }}
                    onChange={(e: any) => {
                      setCheckoutPoints(
                        e.target.value >= 0 ? e.target.value : null,
                      );
                    }}
                  />
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Stack direction="row" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            {/* <Tooltip title={tooltipComponent("")}>
              <InfoIcon fontSize="small" />
            </Tooltip> */}
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateRoomDefinition : addRoomDefinition}
              id="addNewRoomDefinition"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Room
              Definition
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Room Definition Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {roomDefinitionInformation.length > 0 ? (
                <DraggableItemList
                  itemData={roomDefinitionInformation}
                  onOrderUpdate={(order) => setRoomDefinitionInformation(order)}
                  onEdit={(info) => {
                    editRoomDefinition(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRoomDefinition(id)}
                  code={DNDFieldsEnum.ROOMDEFINITIONCODE}
                  name={DNDFieldsEnum.ROOMDEFINITIONNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Room Definition Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RoomtypeDefinition;
